import React from 'react';

import { useConfiguration } from '../../../context/configurationContext';
import { useIntl } from '../../../util/reactIntl';

import SortByPlain from './SortByPlain';
import SortByPopup from './SortByPopup';
import { ReactComponent as SortIcon } from '../../../assets/SortIcon.svg';
import { resolveInitialSortKey } from '../sortUtils';

import css from './SortBy.module.css';

/**
 * SortBy component
 *
 * @component
 * @param {Object} props
 * @param {string} [props.sort] - The sort
 * @param {boolean} [props.showAsPopup] - Whether to show as popup
 * @param {boolean} props.isConflictingFilterActive - Whether the conflicting filter is active
 * @param {boolean} [props.hasConflictingFilters] - Whether there are filters that could conflict
 * @param {'mobile' | 'desktop'} [props.mode] - The mode
 * @returns {JSX.Element}
 */
const SortBy = props => {
  const config = useConfiguration();
  const intl = useIntl();
  const {
    sort,
    showAsPopup = false,
    selectedFilters,
    isConflictingFilterActive,
    hasConflictingFilters,
    mode = 'desktop',
    triggerLabel,
    ...rest
  } = props;

  const { relevanceKey, queryParamName, defaultKey } = config.search.sortConfig;

  const baseMenuClasses = {
    menuLabelRootClassName: css.sortByMenuLabel,
  };
  const layoutClasses =
    mode === 'mobile'
      ? { rootClassName: css.sortByMobile }
      : { className: css.sortByDesktop };
  const menuClasses = { ...baseMenuClasses, ...layoutClasses };

  // Keyword/AI search orders results by relevance, so lock the other options while it is active.
  const isAiSortActive = sort === relevanceKey;
  const isRelevanceActiveSort = isAiSortActive || isConflictingFilterActive;

  const options = config.search.sortConfig.options.reduce((selected, option) => {
    // The keyword "relevance" key is an internal sort state, not a selectable row.
    // The default option carries the "Relevance" label in every state.
    if (option.key === relevanceKey) return selected;

    const translatedOption = option?.labelTranslationKey
      ? { key: option.key, label: intl.formatMessage({ id: option.labelTranslationKey }) }
      : option;

    return [
      ...selected,
      { ...translatedOption, disabled: isRelevanceActiveSort && option.key !== defaultKey },
    ];
  }, []);

  const defaultValue = defaultKey || 'createdAt';
  const resolvedSortKey =
    resolveInitialSortKey({
      config,
      sort,
      selectedFilters,
      hasConflictingFilters,
      isConflictingFilterActive,
    }) || defaultValue;
  // Relevance maps onto the default option, which shares its label.
  const initialValue = resolvedSortKey === relevanceKey ? defaultValue : resolvedSortKey;
  const sortLabel = intl.formatMessage({ id: 'SortBy.heading' });
  const resolvedTriggerLabel = triggerLabel ?? sortLabel;
  const isMobileMode = mode === 'mobile';
  const triggerLabelForMode = isMobileMode ? '' : resolvedTriggerLabel;
  const triggerIcon = (
    <SortIcon className={css.sortIcon} aria-hidden="true" focusable="false" />
  );
  const componentProps = {
    urlParam: queryParamName,
    label: sortLabel,
    triggerLabel: triggerLabelForMode,
    triggerAriaLabel: resolvedTriggerLabel,
    triggerIcon,
    options,
    initialValue,
    ...menuClasses,
    ...rest,
  };
  return showAsPopup ? <SortByPopup {...componentProps} /> : <SortByPlain {...componentProps} />;
};

export default SortBy;
